/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
// วาง/ย้ายโลโก้บนภาพกราฟิกแคมเปญที่สร้างเสร็จแล้ว (รอบ 2 ข้อ A3 · 25 ต.ค. 2569)
// แสดงภาพต้นฉบับ (ไม่มีโลโก้) + โลโก้ลากได้ · ปุ่มตำแหน่งสำเร็จรูป 6 จุด · ขนาด 3 ระดับ
// กด "วางโลโก้" → onSave({ pos, x, y, size, logo }) ให้หน้าเรียก OPTIONS /campaign_graphic/place_logo (ไม่เสียเครดิต)
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Slider from '@mui/material/Slider';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

const MARGIN = 3; // % ของด้านสั้น เท่ากับ LogoOverlay::MARGIN ฝั่ง server
const SIZE_MIN = 5; // ขนาดโลโก้ % ของด้านสั้น — ช่วงเดียวกับ LogoOverlay::normalize
const SIZE_MAX = 50;

/* ขนาดโลโก้/ขอบคิดจาก "ด้านสั้น" ของภาพเหมือน server (26 ต.ค. 2569) แล้วแปลงเป็น % ของกว้าง/สูงไว้วางในกล่อง
   logoRatio = h/w ของโลโก้ · imgRatio = h/w ของภาพหรือกระดาษ */
export const logoBox = (size, logoRatio, imgRatio) => {
  const ir = imgRatio || 1;
  const sw = Math.min(1, ir); // ด้านสั้น ÷ ความกว้าง
  const sh = Math.min(1, 1 / ir); // ด้านสั้น ÷ ความสูง
  return { w: size * sw, h: size * (logoRatio || 1) * sh, mx: MARGIN * sw, my: MARGIN * sh };
};

/* ตำแหน่งสำเร็จรูป → มุมบนซ้ายเป็น % (ต้องรู้สัดส่วนโลโก้กับภาพก่อน) */
export const presetXY = (pos, size, logoRatio, imgRatio) => {
  const { w, h, mx, my } = logoBox(size, logoRatio, imgRatio);
  const right = 100 - mx - w;
  const bottom = 100 - my - h;
  const cx = (100 - w) / 2;
  return (
    {
      tl: [mx, my],
      tc: [cx, my],
      tr: [right, my],
      bl: [mx, bottom],
      bc: [cx, bottom],
      br: [right, bottom],
    }[pos] || [mx, my]
  );
};

/* "16:9" → สูง/กว้าง */
export const ratioHW = (r) => {
  const [a, b] = String(r || '')
    .split(':')
    .map(Number);
  return a > 0 && b > 0 ? b / a : 1;
};

/* ตำแหน่งที่บันทึกไว้เป็น 0 ได้จริง (ลากชิดขอบ) — `|| MARGIN` เดิมเปลี่ยน 0 เป็น 3% ทุกครั้งที่เปิด (รีเช็ค 26 ต.ค.) */
const num = (v, d) =>
  v === undefined || v === null || v === '' || Number.isNaN(Number(v)) ? d : Number(v);

export default function LogoPlacer({
  open,
  onClose,
  baseUrl,
  paper,
  regen,
  note,
  logos,
  positions,
  sizes,
  value,
  approved,
  busy,
  onSave,
  onRemove,
}) {
  const [logo, setLogo] = useState(value.logo || (logos[0] && logos[0].code) || '');
  const [size, setSize] = useState(value.size || 18);
  const [pos, setPos] = useState(value.pos && value.pos !== 'ai' ? value.pos : 'tl');
  const [xy, setXy] = useState([num(value.x, MARGIN), num(value.y, MARGIN)]);
  const [ratios, setRatios] = useState({ img: 1, logo: 1 });
  const boxRef = useRef(null);
  const drag = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLogo(value.logo || (logos[0] && logos[0].code) || '');
    setSize(value.size || 18);
    setPos(value.pos && value.pos !== 'ai' ? value.pos : 'tl');
    setXy([num(value.x, MARGIN), num(value.y, MARGIN)]);
  }, [open]);

  const logoSel = logos.find((l) => l.code === logo) || null;
  const current = pos === 'custom' ? xy : presetXY(pos, Number(size), ratios.logo, ratios.img || 1);
  const box = logoBox(Number(size), ratios.logo, ratios.img || 1); // ขนาดโลโก้เป็น % ของกว้าง/สูงกล่อง
  const logoH = box.h;

  /* เปลี่ยนขนาด/โลโก้ขณะวางเอง → ดึงกลับเข้าในภาพ ให้ตัวอย่างตรงกับที่ server วางจริง (server ก็ล็อกขอบแบบเดียวกัน)
     ทำเมื่อรู้สัดส่วนจริงของภาพและโลโก้แล้วเท่านั้น — ค่าเริ่ม {1,1} เคยดันตำแหน่งที่บันทึกไว้ขึ้นข้างบนตอนเปิด (ผู้ตรวจรอบแก้ 26 ต.ค.) */
  const [ready, setReady] = useState({ img: false, logo: false });
  useEffect(() => {
    if (pos !== 'custom' || !ready.img || !ready.logo) return;
    setXy(([x, y]) => [
      Math.max(0, Math.min(100 - box.w, x)),
      Math.max(0, Math.min(100 - logoH, y)),
    ]);
  }, [size, box.w, logoH, pos, ready.img, ready.logo]);

  /* โหมดกระดาษ (ตั้งค่าก่อนสร้างภาพ): ยังไม่มีภาพ ใช้สัดส่วนที่เลือกในแผงเป็นรูปทรงกระดาษ */
  const [pw, ph] = String(paper || '1:1')
    .split(':')
    .map(Number);
  useEffect(() => {
    if (!open || !paper) return;
    setRatios((r) => ({ ...r, img: ratioHW(paper) }));
    setReady((r) => ({ ...r, img: true }));
  }, [open, paper]);

  const onImgLoad = (e) => {
    setRatios((r) => ({ ...r, img: e.target.naturalHeight / e.target.naturalWidth }));
    setReady((r) => ({ ...r, img: true }));
  };
  const onLogoLoad = (e) => {
    setRatios((r) => ({ ...r, logo: e.target.naturalHeight / e.target.naturalWidth }));
    setReady((r) => ({ ...r, logo: true }));
  };

  /* ลากโลโก้ด้วยเมาส์/นิ้ว — คิดเป็น % ของกล่องภาพ แล้วล็อกไม่ให้เลยขอบ */
  const startDrag = (e) => {
    if (busy) return;
    const rect = boxRef.current.getBoundingClientRect();
    drag.current = {
      ox: e.clientX - rect.left - (current[0] / 100) * rect.width,
      oy: e.clientY - rect.top - (current[1] / 100) * rect.height,
      rect,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const moveDrag = (e) => {
    if (!drag.current) return;
    const { ox, oy, rect } = drag.current;
    const x = ((e.clientX - rect.left - ox) / rect.width) * 100;
    const y = ((e.clientY - rect.top - oy) / rect.height) * 100;
    setPos('custom');
    setXy([Math.max(0, Math.min(100 - box.w, x)), Math.max(0, Math.min(100 - logoH, y))]);
  };
  const endDrag = (e) => {
    if (drag.current) e.currentTarget.releasePointerCapture(e.pointerId);
    drag.current = null;
  };
  /* ลากมุมล่างขวาเพื่อย่อ/ขยาย (แบบ Canva: มุมบนซ้ายอยู่กับที่) — ตำแหน่งสำเร็จรูปจะกลายเป็น "กำหนดเอง" ที่จุดเดิม
     ขนาดไม่เกินขอบกระดาษ/ภาพ · เจ้าของงานขอ 26 ต.ค. 2569 "สามารถย่อ ขยาย ขนาดโลโก้ได้" */
  const resize = useRef(null);
  const startResize = (e) => {
    if (busy) return;
    e.stopPropagation();
    resize.current = { rect: boxRef.current.getBoundingClientRect(), x: current[0], y: current[1] };
    setPos('custom');
    setXy([current[0], current[1]]);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const moveResize = (e) => {
    if (!resize.current) return;
    e.stopPropagation();
    const { rect, x, y } = resize.current;
    const unit = logoBox(1, ratios.logo, ratios.img || 1); // % กว้าง/สูงต่อขนาด 1 หน่วย
    /* ลากได้ทั้งแนวนอนและแนวตั้ง (เอาด้านที่ลากไกลกว่า) · ปัดเพดานลงไม่ให้เกินขอบแล้วมุมบนซ้ายขยับ (รีเช็ค 27 ต.ค. 2569) */
    const wantW = ((e.clientX - rect.left) / rect.width) * 100 - x;
    const wantH = ((e.clientY - rect.top) / rect.height) * 100 - y;
    const fitMax = Math.floor(Math.min((100 - x) / unit.w, (100 - y) / unit.h, SIZE_MAX));
    const want = Math.round(Math.max(wantW / unit.w, wantH / unit.h));
    setSize(Math.max(SIZE_MIN, Math.min(fitMax, want)));
  };
  const endResize = (e) => {
    if (resize.current) e.currentTarget.releasePointerCapture(e.pointerId);
    resize.current = null;
  };
  /* กดจุดไหนบนภาพ = วางโลโก้ให้กึ่งกลางอยู่ตรงนั้น (เจ้าของงานถาม 25 ต.ค. 2569 "กดเป็นจุดได้เลย") · เป็น % จึงตรงทุกสเกล */
  const placeAt = (e) => {
    if (busy || !logoSel) return;
    const rect = boxRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100 - box.w / 2;
    const y = ((e.clientY - rect.top) / rect.height) * 100 - logoH / 2;
    setPos('custom');
    setXy([Math.max(0, Math.min(100 - box.w, x)), Math.max(0, Math.min(100 - logoH, y))]);
  };

  const save = () =>
    onSave({
      logo,
      pos,
      x: pos === 'custom' ? Number(current[0].toFixed(2)) : 0,
      y: pos === 'custom' ? Number(current[1].toFixed(2)) : 0,
      size: Number(size),
    });

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth='md'>
      <DialogTitle>
        {paper
          ? `${regen ? 'สร้างใหม่พร้อมโลโก้' : 'เลือกจุดวางโลโก้'} · กระดาษ ${paper}`
          : regen
          ? 'ใส่โลโก้ด้วย AI · สร้างภาพใหม่'
          : 'วางโลโก้บนภาพ · ไม่เสียเครดิต'}
      </DialogTitle>
      <DialogContent dividers>
        <MDBox display='flex' gap={2} flexWrap='wrap'>
          <MDBox flex='1 1 420px' minWidth={0}>
            {/* กล่องภาพ: ภาพต้นฉบับ (หรือกระดาษเปล่าตามสัดส่วนที่เลือก) + โลโก้ซ้อนทับแบบลากได้ · ขนาดคิดจากด้านสั้นเหมือน server */}
            <MDBox
              ref={boxRef}
              sx={{
                position: 'relative',
                width: paper ? `min(100%, calc(60vh * ${pw} / ${ph}))` : 'fit-content',
                aspectRatio: paper ? `${pw} / ${ph}` : undefined,
                maxWidth: '100%',
                mx: 'auto',
                lineHeight: 0,
                bgcolor: paper ? '#fff' : 'grey.100',
                border: paper ? '1px solid #d2d6da' : undefined,
                backgroundImage: paper
                  ? 'linear-gradient(to right, rgba(41,52,122,.10) 1px, transparent 1px), linear-gradient(to bottom, rgba(41,52,122,.10) 1px, transparent 1px)'
                  : undefined,
                backgroundSize: paper ? '33.333% 33.333%' : undefined,
                borderRadius: 1,
                overflow: 'hidden',
                userSelect: 'none',
                touchAction: 'none',
              }}
            >
              {paper && (
                /* กระดาษ: กดจุดไหน = วางโลโก้กึ่งกลางตรงนั้น · เส้นแบ่ง 3 ส่วนช่วยกะตำแหน่ง */
                /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
                <div
                  onClick={placeAt}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end',
                    padding: 6,
                    cursor: 'crosshair',
                  }}
                >
                  <span style={{ color: 'rgba(41,52,122,.35)', fontSize: 12, lineHeight: 1.4 }}>
                    กระดาษ {paper}
                  </span>
                </div>
              )}
              {/* คีย์บอร์ดใช้ปุ่ม 6 ตำแหน่งด้านขวาแทนการกดจุด */}
              {!paper && (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                <img
                  src={baseUrl}
                  alt='ภาพต้นฉบับไม่มีโลโก้ กดจุดที่ต้องการวางโลโก้'
                  onLoad={onImgLoad}
                  onClick={placeAt}
                  /* ภาพแนวตั้ง (4:5 · 9:16) เคยสูงเกินจอ 1366×768 ตำแหน่งล่างตกขอบ — จำกัดความสูง กล่องหดตามภาพ % จึงยังตรง */
                  style={{
                    display: 'block',
                    maxWidth: '100%',
                    maxHeight: '60vh',
                    width: 'auto',
                    height: 'auto',
                    cursor: busy ? 'default' : 'crosshair',
                  }}
                  draggable={false}
                />
              )}
              {logoSel && (
                <MDBox
                  role='button'
                  tabIndex={0}
                  aria-label='โลโก้ ลากเพื่อย้ายตำแหน่ง'
                  onPointerDown={startDrag}
                  onPointerMove={moveDrag}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                  sx={{
                    position: 'absolute',
                    left: `${current[0]}%`,
                    top: `${current[1]}%`,
                    width: `${box.w}%`,
                    cursor: busy ? 'default' : 'grab',
                    outline: '2px dashed rgba(41,52,122,.8)',
                    outlineOffset: 2,
                    lineHeight: 0,
                  }}
                >
                  <img
                    src={logoSel.image}
                    alt={logoSel.name}
                    onLoad={onLogoLoad}
                    style={{ width: '100%', display: 'block', pointerEvents: 'none' }}
                    draggable={false}
                  />
                  {/* จับมุมล่างขวาลากเพื่อย่อ/ขยาย · คีย์บอร์ดใช้แถบเลื่อนขนาดด้านขวาแทน */}
                  <MDBox
                    aria-hidden
                    title='ลากเพื่อย่อ/ขยายโลโก้'
                    onPointerDown={startResize}
                    onPointerMove={moveResize}
                    onPointerUp={endResize}
                    onPointerCancel={endResize}
                    sx={{
                      position: 'absolute',
                      /* อยู่ในมุมโลโก้ — โลโก้ชิดขอบกระดาษแล้ววงกลมเคยถูกตัดครึ่ง (ตรวจใน Chrome 27 ต.ค.) */
                      right: -1,
                      bottom: -1,
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      bgcolor: '#fff',
                      border: '2px solid rgba(41,52,122,.9)',
                      cursor: busy ? 'default' : 'nwse-resize',
                      touchAction: 'none',
                    }}
                  />
                </MDBox>
              )}
            </MDBox>
            <MDTypography variant='caption' color='text' display='block' mt={0.5}>
              {paper
                ? regen
                  ? 'กดจุดบนกระดาษเพื่อวางโลโก้ · ลากปรับได้ · AI จะออกแบบภาพใหม่ทั้งภาพจากคำสั่งเดิมของงานนี้ โดยมีโลโก้ตรงจุดนี้ตั้งแต่แรก (ภาพเดิมยังอยู่)'
                  : 'กดจุดบนกระดาษเพื่อวางโลโก้ · ลากปรับได้ · ระบบแนบกระดาษนี้ให้ AI เป็นผังตำแหน่ง แล้วสร้างภาพที่มีโลโก้อยู่ตรงจุดนี้'
                : regen
                ? 'กดจุดบนภาพเพื่อวางโลโก้ · ลากปรับได้ · AI จะสร้างภาพใหม่ที่มีโลโก้อยู่ในงานตรงจุดนี้ (ภาพเดิมยังอยู่)'
                : 'กดจุดบนภาพเพื่อวางโลโก้ตรงนั้น · ลากโลโก้ปรับได้ · หรือกดตำแหน่งสำเร็จรูป 6 จุดด้านขวา (ล็อกตามขอบภาพทุกขนาด)'}
            </MDTypography>
            {note && (
              <MDTypography
                variant='caption'
                color='dark'
                fontWeight='medium'
                display='block'
                mt={0.5}
              >
                {note}
              </MDTypography>
            )}
          </MDBox>
          <MDBox flex='0 0 220px' display='flex' flexDirection='column' gap={1.5}>
            {logos.length > 1 && (
              <MDInput
                label='โลโก้'
                select
                fullWidth
                size='small'
                value={logo}
                onChange={(e) => {
                  setReady((r) => ({ ...r, logo: false })); // รอสัดส่วนของโลโก้ใหม่ก่อนดึงเข้าขอบ
                  setLogo(e.target.value);
                }}
                InputLabelProps={{ shrink: true }}
              >
                {logos.map((l) => (
                  <MenuItem key={l.code} value={l.code}>
                    {l.name}
                  </MenuItem>
                ))}
              </MDInput>
            )}
            <MDTypography variant='caption' fontWeight='bold'>
              ตำแหน่ง
            </MDTypography>
            <MDBox display='grid' gridTemplateColumns='1fr 1fr 1fr' gap={0.5}>
              {positions
                .filter((p) => p.code !== 'ai' && p.code !== 'custom')
                .map((p) => (
                  <MDButton
                    key={p.code}
                    size='small'
                    variant={pos === p.code ? 'gradient' : 'outlined'}
                    color='dark'
                    onClick={() => setPos(p.code)}
                    aria-pressed={pos === p.code}
                    sx={{ minWidth: 0, px: 0.5 }}
                  >
                    {p.name}
                  </MDButton>
                ))}
            </MDBox>
            {pos === 'custom' && (
              <MDTypography variant='caption' color='text'>
                กำหนดเอง · {current[0].toFixed(0)}% , {current[1].toFixed(0)}%
              </MDTypography>
            )}
            <MDBox>
              <MDTypography variant='caption' fontWeight='bold' display='block'>
                ขนาด · {Number(size)}%
              </MDTypography>
              <MDBox px={1}>
                <Slider
                  value={Number(size)}
                  min={SIZE_MIN}
                  max={SIZE_MAX}
                  step={1}
                  marks={sizes.map((s) => ({ value: s.value }))}
                  onChange={(e, v) => setSize(Number(v))}
                  disabled={busy}
                  getAriaValueText={(v) => `${v}%`}
                  aria-label='ขนาดโลโก้'
                />
              </MDBox>
              <MDBox display='flex' gap={0.5}>
                {sizes.map((s) => (
                  <MDButton
                    key={s.value}
                    size='small'
                    variant={Number(size) === s.value ? 'gradient' : 'text'}
                    color='dark'
                    onClick={() => setSize(s.value)}
                    aria-pressed={Number(size) === s.value}
                    sx={{ minWidth: 0, px: 1 }}
                  >
                    {s.name}
                  </MDButton>
                ))}
              </MDBox>
            </MDBox>
            {approved && (
              <MDTypography variant='caption' color='warning'>
                ภาพนี้เก็บเข้ารูปแคมเปญแล้ว — สำเนาที่เก็บไว้ไม่เปลี่ยนตาม ต้องเก็บใหม่
              </MDTypography>
            )}
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <MDBox>
          {value.logo && onRemove && (
            <MDButton variant='text' color='error' disabled={busy} onClick={onRemove}>
              เอาโลโก้ออก
            </MDButton>
          )}
        </MDBox>
        <MDBox display='flex' gap={1}>
          <MDButton variant='outlined' color='secondary' disabled={busy} onClick={onClose}>
            ยกเลิก
          </MDButton>
          <MDButton variant='gradient' color='dark' disabled={busy || !logoSel} onClick={save}>
            {paper
              ? regen
                ? busy
                  ? 'กำลังส่งงาน…'
                  : regen
                : 'ใช้ตำแหน่งนี้'
              : regen
              ? busy
                ? 'กำลังส่งงาน…'
                : regen
              : busy
              ? 'กำลังวาง…'
              : 'วางโลโก้'}
          </MDButton>
        </MDBox>
      </DialogActions>
    </Dialog>
  );
}

LogoPlacer.defaultProps = {
  baseUrl: '',
  paper: '',
  regen: '',
  note: '',
  value: { logo: '', pos: '', x: 0, y: 0, size: 0 },
  approved: false,
  busy: false,
  onRemove: null,
};
LogoPlacer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  baseUrl: PropTypes.string, // ภาพที่เสร็จแล้ว (ย้ายโลโก้หลังเจน)
  paper: PropTypes.string, // สัดส่วน เช่น '16:9' = โหมดกระดาษ เลือกจุดก่อนสร้างภาพ (ไม่มี baseUrl)
  regen: PropTypes.string, // ข้อความปุ่มยืนยันเมื่อใส่โลโก้ด้วยการสร้างใหม่ เช่น 'สร้างภาพใหม่ · 6 เครดิต' (ว่าง = วางทับแบบเดิม)
  note: PropTypes.string, // บรรทัดเสริมใต้คำอธิบาย เช่น รูปอ้างอิงที่จะส่งไปกับงานสร้างใหม่
  logos: PropTypes.arrayOf(
    PropTypes.shape({ code: PropTypes.string, name: PropTypes.string, image: PropTypes.string })
  ).isRequired,
  positions: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string, name: PropTypes.string }))
    .isRequired,
  sizes: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number, name: PropTypes.string }))
    .isRequired,
  value: PropTypes.shape({
    logo: PropTypes.string,
    pos: PropTypes.string,
    x: PropTypes.number,
    y: PropTypes.number,
    size: PropTypes.number,
  }),
  approved: PropTypes.bool,
  busy: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
};
